import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import feedbackService from '../services/FeedbackService';
import { Icon } from './Icon.js';
import './FeedbackBox.css';

const FeedbackBox = ({ isOpen, onClose, isPageMode = false }) => {
  const [type, setType] = useState('feedback');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState('normal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status (use getSession to avoid extra GET /auth/v1/user)
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session?.user);
    };

    if (isOpen) {
      checkAuth();
    }
  }, [isOpen]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset form state when closing
      setType('feedback');
      setTitle('');
      setContent('');
      setPriority('normal');
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  const feedbackTypes = [
    { value: 'feedback', label: 'General Feedback', icon: 'MessageSquare', description: 'Share your thoughts about the app' },
    { value: 'suggestion', label: 'Suggestion', icon: 'Lightbulb', description: 'Suggest a new feature or improvement' },
    { value: 'bug_report', label: 'Bug Report', icon: 'AlertCircle', description: 'Report a bug or issue you encountered' },
    { value: 'biohack_request', label: 'Biohack Request', icon: 'PlusCircle', description: 'Request a new biohack to be added' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate content
    if (!content.trim()) {
      setError('Please provide your feedback or suggestion.');
      return;
    }

    if (content.trim().length < 10) {
      setError('Please provide more details (at least 10 characters).');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await feedbackService.submitFeedback({
        type,
        title: title.trim() || null,
        content: content.trim(),
        priority
      });

      if (result.success) {
        setSuccess(true);
        // Reset form
        setTitle('');
        setContent('');
        // Close modal after showing success message
        setTimeout(() => {
          handleClose();
        }, 2500);
      } else {
        setError(result.message || 'Failed to submit feedback. Please try again.');
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    }

    setIsSubmitting(false);
  };

  const handleClose = useCallback(() => {
    // Reset form state
    setType('feedback');
    setTitle('');
    setContent('');
    setPriority('normal');
    setError('');
    setSuccess(false);
    onClose();
  }, [onClose]);

  // Click outside to close functionality (disabled in page mode)
  useEffect(() => {
    // Don't add click-outside listener when in page mode
    if (isPageMode) return;
    
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.feedback-modal')) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose, isPageMode]);

  // Handle Escape key (disabled in page mode)
  useEffect(() => {
    // Don't add escape key listener when in page mode
    if (isPageMode) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose, isPageMode]);

  if (!isOpen) return null;

  return (
    <>
      {!isPageMode && <div className="feedback-modal-overlay" onClick={handleClose} />}
      <div 
        className={`feedback-modal ${isPageMode ? 'feedback-page-mode' : ''}`} 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="feedback-modal-header">
          <div className="feedback-modal-title">
            <div className="feedback-modal-icon-wrapper">
              <Icon name="MessageCircleQuestionMark" className="feedback-modal-icon" />
            </div>
            <h2>Feedback & Suggestions</h2>
          </div>
          {!isPageMode && (
            <button className="feedback-modal-close" onClick={handleClose} aria-label="Close">
              <Icon name="X" className="icon--sm" />
            </button>
          )}
        </div>

        <div className="feedback-modal-content">
          {success ? (
            <div className="feedback-success">
              <Icon name="CheckCircle" className="icon--lg icon--success" />
              <h3>Thank You!</h3>
              <p>Your feedback has been submitted successfully. We appreciate your input!</p>
            </div>
          ) : (
            <>
              {!isAuthenticated && (
                <div className="feedback-auth-notice">
                  <Icon name="Info" className="icon--sm" />
                  <span>You can submit feedback anonymously, or sign in to track your submissions.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="feedback-form">
                <div className="feedback-type-selection">
                  <label className="feedback-form-label">
                    What type of feedback is this?
                  </label>
                  <div className="feedback-types-grid">
                    {feedbackTypes.map((feedbackType) => (
                      <button
                        key={feedbackType.value}
                        type="button"
                        className={`feedback-type-option ${type === feedbackType.value ? 'active' : ''}`}
                        onClick={() => setType(feedbackType.value)}
                      >
                        <Icon name={feedbackType.icon} className="icon--sm" />
                        <div className="feedback-type-info">
                          <div className="feedback-type-label">{feedbackType.label}</div>
                          <div className="feedback-type-description">{feedbackType.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="feedback-title-field">
                  <label className="feedback-form-label" htmlFor="feedback-title">
                    Title (Optional)
                  </label>
                  <input
                    id="feedback-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Brief summary of your feedback..."
                    className="feedback-title-input"
                    maxLength={100}
                  />
                </div>

                <div className="feedback-content-field">
                  <label className="feedback-form-label" htmlFor="feedback-content">
                    Your Feedback <span className="required">*</span>
                  </label>
                  <textarea
                    id="feedback-content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder={
                      type === 'bug_report' 
                        ? 'Please describe the bug, steps to reproduce it, and what you expected to happen...'
                        : type === 'biohack_request'
                        ? 'Please describe the biohack you would like to see added and how it would be useful...'
                        : type === 'suggestion'
                        ? 'Please share your suggestion and how it would improve the app...'
                        : 'Share your thoughts, feedback, or ideas...'
                    }
                    className="feedback-content-textarea"
                    rows={6}
                    required
                  />
                  <div className="feedback-char-count">
                    {content.length} characters (minimum 10)
                  </div>
                </div>

                {error && (
                  <div className="feedback-error">
                    <Icon name="AlertCircle" className="icon--sm" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="feedback-form-actions">
                  <button
                    type="button"
                    className="feedback-cancel-btn"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="feedback-submit-btn"
                    disabled={isSubmitting || !content.trim() || content.trim().length < 10}
                  >
                    {isSubmitting ? (
                      <>
                        <Icon name="Loader2" className="icon--sm spinning" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Send" className="icon--sm" />
                        <span>Submit</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedbackBox;